import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QuestionStep from './InputQuestionStep'; 
import FileUploadStep from './FileUploadStep'; 
import ConfigSummaryStep from './ConfigSummaryStep.jsx';
import CategoriesStep from './CategoriesStep.jsx';

// Custom Hook to persist state to sessionStorage
function useSessionStorage(key, initialValue) {
  // Get initial value from storage, or fallback to initialValue
  const [value, setValue] = useState(() => {
    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error("Error reading sessionStorage", error);
      return initialValue;
    }
  });

  // Update storage whenever the value changes
  useEffect(() => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error("Error setting sessionStorage", error);
    }
  }, [key, value]);

  return [value, setValue];
}

const CreateAnalysisPage = ({ updateBalance }) => {
  window.dataLayer = window.dataLayer || [];
  const navigate = useNavigate();
  const [question, setQuestion] = useSessionStorage('wizard_question', '');
  const [isListBrands, setIsListBrands] = useSessionStorage('wizard_type', null);

  const [searchParams] = useSearchParams();
  const step = parseInt(searchParams.get('step') || '1', 10);
  const [currentFileId, setCurrentFileId] = useState(searchParams.get('fileId') || null);

  const goToStep = (nextStep, nextFileId = currentFileId) => {
    const params = new URLSearchParams();
    params.set('step', nextStep);
    
    if (nextFileId) {
      params.set('fileId', nextFileId);
    }

    navigate(`/analysis/create?${params.toString()}`);
  };

  // Cleanup function to clear data when the analysis is finished
  const clearWizardData = () => {
    sessionStorage.removeItem('wizard_question');
    sessionStorage.removeItem('wizard_type');
  };

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    if (step === 1) {
      window.dataLayer.push({ event: 'step_question' });
    }
    if (step === 2) {
      // not here - here triggered too often
      // window.dataLayer.push({ event: 'step_data' });
    }
    if (step === 3) {
      // not here - needs cost
      // window.dataLayer.push({ event: 'step_summary' });
    }
    if (step === 4) {
      window.dataLayer.push({ event: 'step_categories' });
    }
  }, [step]);

  return (
    <div className="flex justify-center min-h-full p-4 pt-8 pb-16">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl p-8 md:p-12 transform transition-all duration-500">
        <div className="flex flex-col items-center">
            {step === 1 && (
                <QuestionStep 
                    question={question} 
                    setQuestion={setQuestion} 
                    onNext={() => goToStep(2)} 
                />
            )}
            {step === 2 && (
                <FileUploadStep 
                    setFileId={setCurrentFileId}
                    question={question}
                    onBack={() => goToStep(1)} 
                    onNext={() => {window.dataLayer.push({ event: 'step_data' }); goToStep(3);}} 
                />
            )}
            {step === 3 && (
                <ConfigSummaryStep
                    question={question}
                    isListBrands={isListBrands}
                    setIsListBrands={setIsListBrands}
                    fileId={currentFileId}
                    onBack={() => goToStep(2)}
                    onNext={() => goToStep(4)}
                />
            )}
            {step === 4 && (
                <CategoriesStep
                    onShowResult={(analysisId) => {
                        clearWizardData(); 
                        navigate(`/analysis/${analysisId}/summary`);
                    }}
                    fileId={currentFileId}
                    updateBalance={updateBalance}
                    question={question}
                />
            )}
        </div>
      </div>
    </div>
  );
};
export default CreateAnalysisPage;